/**
 * Session health — notice a dead or wrong Firebase identity under a logged-in tab, and heal it.
 *
 * ── Why this exists (2026-10-08 Patient Profile permission-denied report) ──────────────────────
 * The SPA's only session gate is localStorage `authenticated`, which never expires. The Firebase
 * identity behind it can die on its own: a teamio-admin "Revoke Users" / deactivation revokes the
 * refresh tokens, or the browser loses Firebase's IndexedDB copy. A tab that was closed or asleep at
 * the revoke never sees the `metadata/<uid>` logout signal (reading it needs a live identity), so it
 * comes back "logged in" with no Firebase user — a ZOMBIE session. The open HTTP API keeps working,
 * so nothing looks wrong, but every Firestore read is denied: tasks show "No open tasks", and the
 * kanban / Practice Pulse listeners paint a raw FirebaseError banner on whatever page is open.
 * Fleet sizing the same day: 45 of 271 active staff had been revoked since their last sign-in.
 * Design: docs/plans/2026-10-08-firebase-session-health-design.md.
 *
 * ── What this module decides ────────────────────────────────────────────────────────────────────
 * classifySession()   → OK | SITE_MISMATCH | NO_CLAIMS | DEAD | OFFLINE
 * createSessionHealth → one check() that classifies, then heals:
 *   SITE_MISMATCH / NO_CLAIMS → re-run /select-site for this tab's site, then reload
 *   DEAD                      → silent Microsoft re-auth, then reload; else a "sign in again" prompt
 *   OFFLINE                   → nothing (a network blip is never a dead session)
 *   DEMO_INVALID, or anything wrong with a demo session (Try Teamio) → the prompt only, never a heal
 * At most ONE automatic heal per tab per HEAL_COOLDOWN_MS (sessionStorage stamp), so a broken
 * backend can never cause a reload storm — after that, DEAD only gets the prompt.
 *
 * reportSessionProblem() is the one door for "something just got denied": the Firestore listener
 * wrapper (src/store/firestoreListen.js) and the 401 interceptor call it; the guard registers the
 * handler (mirrors versionCheck's setStaleBundleHandler/reportStaleBundle).
 *
 * ── Purity ───────────────────────────────────────────────────────────────────────────────────────
 * ZERO imports on purpose: cypress/node-tests/session-health-core.test.js executes this exact file
 * via loadEsmUtil. Firebase, MSAL, the store, the router and storage are all injected by the thin
 * wiring in src/plugins/sessionHealthGuard.js. Keep it import-free.
 */

export const HEAL_COOLDOWN_MS = 10 * 60 * 1000;
export const HEAL_STAMP_KEY = 'teamio_session_heal_at';

/** Firebase auth error codes that mean "the network failed", never "the session is dead". */
const NETWORK_CODES = ['auth/network-request-failed', 'auth/timeout', 'unavailable'];

/**
 * Classify the tab's Firebase session.
 *
 * @param {object} s
 * @param {object|null} s.user            the Firebase user (null = no identity at all)
 * @param {object}      [s.claims]        the ID token's claims (from getIdTokenResult)
 * @param {object}      [s.tokenError]    the error the token fetch threw, if it threw
 * @param {string}      [s.expectedSiteID] the site THIS tab is on
 * @param {string}      [s.demoSiteID]     the demo site (Try Teamio); a demo token is OK only there
 * @returns {'OK'|'SITE_MISMATCH'|'NO_CLAIMS'|'DEMO_INVALID'|'DEAD'|'OFFLINE'}
 */
export function classifySession(s) {
  const { user, claims, tokenError, expectedSiteID, demoSiteID } = s || {};
  if (tokenError) {
    return NETWORK_CODES.includes(tokenError.code) ? 'OFFLINE' : 'DEAD';
  }
  if (!user) return 'DEAD';
  // A demo token (demo-service) never carries isCustomer; it is healthy on the demo site, on the demo tab.
  if (claims && claims.demo === true) {
    const onDemo = !!demoSiteID && claims.siteID === demoSiteID && (!expectedSiteID || expectedSiteID === demoSiteID);
    return onDemo ? 'OK' : 'DEMO_INVALID';
  }
  if (!claims || claims.isCustomer !== true) return 'NO_CLAIMS';
  if (expectedSiteID && claims.siteID !== expectedSiteID) return 'SITE_MISMATCH';
  return 'OK';
}

/** Is this Firestore error a rules denial (as opposed to a missing index, a network blip, …)? */
export function isPermissionDenied(err) {
  if (!err) return false;
  if (err.code === 'permission-denied') return true;
  return /missing or insufficient permissions/i.test(err.message || '');
}

let problemHandler = null;

/** The guard hands us its check(); null when no guard is mounted (public pages, logged out). */
export function setSessionProblemHandler(fn) {
  problemHandler = typeof fn === 'function' ? fn : null;
}

/** "Something was just denied" — a no-op when no guard is mounted. Never throws. */
export function reportSessionProblem(trigger) {
  if (!problemHandler) return;
  try {
    problemHandler(trigger);
  } catch (_e) {
    /* a reporter must never break its caller */
  }
}

/**
 * The error callback every Firestore listener gets (via src/store/firestoreListen.js).
 * A denial is reported to the session guard instead of being painted raw; every error is logged
 * and handed to the caller's onError(err, { denied }) so a card can show "couldn't load".
 * Never throws.
 *
 * @returns {'denied'|'other'}
 */
export function handleSnapshotError(err, opts) {
  const { source, onError, report, log } = opts || {};
  const denied = isPermissionDenied(err);
  try {
    if (log) log(`[firestore] ${source || 'listener'} ${denied ? 'denied' : 'error'}:`, err);
  } catch (_e) { /* logging must not break the callback */ }
  if (denied) (report || reportSessionProblem)(`denied:${source || 'listener'}`);
  if (typeof onError === 'function') {
    try {
      onError(err, { denied });
    } catch (e) {
      try { if (log) log(`[firestore] ${source || 'listener'} onError threw:`, e); } catch (_e) { /* ignore */ }
    }
  }
  return denied ? 'denied' : 'other';
}

/**
 * Build the guard's check(). All environment is injected.
 *
 * @param {object} deps
 * @param {() => boolean} deps.isEnabled            kill switch (false = do nothing)
 * @param {() => boolean} deps.isAuthenticated      localStorage `authenticated` === 'true'
 * @param {() => boolean} deps.isAuthedRoute        current route requires auth (never act on public pages)
 * @param {() => Promise<object|null>} deps.getUser waits out Firebase's async init
 * @param {(user, force: boolean) => Promise<{claims}>} deps.getTokenResult
 * @param {() => string|null} deps.expectedSiteID
 * @param {string} [deps.demoSiteID]                   the demo site (src/utils/demo/demoSession.js)
 * @param {() => boolean} [deps.isDemoSession]         this tab is a demo session (store flag, from the token)
 * @param {() => Promise<{ok: boolean}>} deps.reselectSite   /select-site for this tab's site
 * @param {() => Promise<boolean>} deps.silentReauth          no-popup re-auth; true = signed back in
 * @param {() => void} deps.reload
 * @param {(state: string) => void} deps.showSignInPrompt
 * @param {{get: (k) => string|null, set: (k, v) => void}} deps.storage   sessionStorage
 * @param {() => number} [deps.now]
 * @param {(outcome: string, trigger: string) => void} [deps.onOutcome]
 */
export function createSessionHealth(deps) {
  let inflight = null;
  const now = () => (deps.now ? deps.now() : Date.now());

  async function probe(force) {
    const user = await deps.getUser();
    if (!user) return classifySession({ user: null });
    let result;
    try {
      result = await deps.getTokenResult(user, force);
    } catch (tokenError) {
      return classifySession({ user, tokenError });
    }
    return classifySession({
      user, claims: result && result.claims, expectedSiteID: deps.expectedSiteID(), demoSiteID: deps.demoSiteID,
    });
  }

  function cooldownActive() {
    let last = 0;
    try { last = Number(deps.storage.get(HEAL_STAMP_KEY)) || 0; } catch (_e) { last = 0; }
    return now() - last < HEAL_COOLDOWN_MS;
  }

  function stampHeal() {
    try { deps.storage.set(HEAL_STAMP_KEY, String(now())); } catch (_e) { /* storage blocked */ }
  }

  async function run(trigger) {
    if (!deps.isEnabled()) return 'skipped:disabled';
    if (!deps.isAuthenticated()) return 'skipped:not-authenticated';
    if (!deps.isAuthedRoute()) return 'skipped:public-route';

    // A denial or a 401 suggests the CLAIMS may be stale, so force a fresh token; a boot or
    // visibility check takes the cached one (getIdTokenResult refreshes it anyway when expired).
    const force = /^(denied|http-401)/.test(String(trigger));
    const state = await probe(force);
    if (state === 'OK') return 'ok';
    if (state === 'OFFLINE') return 'offline';

    // A demo session is never healed: /select-site refuses it (server, decision 6a) and it has no Microsoft
    // account to re-auth with. It only gets the prompt, which ends the demo session (logout → /try).
    if (state === 'DEMO_INVALID' || (deps.isDemoSession && deps.isDemoSession())) {
      deps.showSignInPrompt(state);
      return `prompt:demo:${state}`;
    }

    if (cooldownActive()) {
      deps.showSignInPrompt(state);
      return `prompt:cooldown:${state}`;
    }
    stampHeal();

    if (state === 'SITE_MISMATCH' || state === 'NO_CLAIMS') {
      const r = await deps.reselectSite();
      if (r && r.ok && (await probe(true)) === 'OK') {
        deps.reload();
        return `healed:reselect:${state}`;
      }
      deps.showSignInPrompt(state);
      return `prompt:reselect-failed:${state}`;
    }

    // DEAD
    const signedIn = await deps.silentReauth();
    if (signedIn && (await probe(true)) === 'OK') {
      deps.reload();
      return 'healed:silent';
    }
    deps.showSignInPrompt(state);
    return 'prompt:DEAD';
  }

  async function guarded(trigger) {
    let outcome;
    try {
      outcome = await run(trigger);
    } catch (_e) {
      outcome = 'error';
    }
    try { if (deps.onOutcome) deps.onOutcome(outcome, trigger); } catch (_e) { /* ignore */ }
    return outcome;
  }

  return {
    /** Run one check. Concurrent callers share the in-flight run. Never throws. */
    check(trigger) {
      if (inflight) return inflight;
      inflight = guarded(trigger).then((o) => { inflight = null; return o; });
      return inflight;
    },
    /** True while a check (and any heal it started) is running. */
    isBusy() {
      return inflight !== null;
    },
  };
}
